<template>
    <div>
        <Head :title="__('Indicadores')" />

        <!-- ===== FILTROS ===== -->
        <div class="bg-white rounded-md shadow p-4 mb-5">
            <div class="font-semibold text-sm mb-3">{{ __('Filtrar por') }}:</div>
            <div class="flex flex-wrap gap-3 items-end">
                <div class="flex flex-col gap-1 min-w-[150px]">
                    <label class="text-xs text-gray-500">{{ __('Date Start') }}</label>
                    <input
                        type="date"
                        v-model="form.fini"
                        class="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                </div>
                <div class="flex flex-col gap-1 min-w-[150px]">
                    <label class="text-xs text-gray-500">{{ __('Date End') }}</label>
                    <input
                        type="date"
                        v-model="form.fend"
                        class="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                </div>
                <div class="flex flex-col gap-1 min-w-[160px]">
                    <label class="text-xs text-gray-500">{{ __('Sede del colegio') }}</label>
                    <select
                        v-model="form.department_id"
                        class="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option :value="null">{{ __('Todas las sedes') }}</option>
                        <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                </div>
                <div class="flex flex-col gap-1 min-w-[160px]">
                    <label class="text-xs text-gray-500">{{ __('Tipo de Ticket') }}</label>
                    <select
                        v-model="form.type_id"
                        class="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option :value="null">{{ __('Todos los tipos') }}</option>
                        <option v-for="t in types" :key="t.id" :value="t.id">{{ t.name }}</option>
                    </select>
                </div>
                <div class="flex flex-col gap-1 min-w-[160px]">
                    <label class="text-xs text-gray-500">{{ __('Priority') }}</label>
                    <select
                        v-model="form.priority_id"
                        class="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option :value="null">{{ __('Todas las prioridades') }}</option>
                        <option v-for="p in priorities" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                </div>
                <div class="flex flex-col gap-1 min-w-[160px]">
                    <label class="text-xs text-gray-500">{{ __('Status') }}</label>
                    <select
                        v-model="form.status_id"
                        class="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option :value="null">{{ __('Todos los estados') }}</option>
                        <option v-for="s in statuses" :key="s.id" :value="s.id">{{ s.name }}</option>
                    </select>
                </div>
                <div class="flex flex-col gap-1 min-w-[160px]">
                    <label class="text-xs text-gray-500">{{ __('Sección') }}</label>
                    <select
                        v-model="form.category_id"
                        class="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option :value="null">{{ __('Todas las secciones') }}</option>
                        <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                </div>
                <div v-if="!onlyMine && responsables.length > 0" class="flex flex-col gap-1 min-w-[200px]">
                    <label class="text-xs text-gray-500">{{ __('Responsable') }}</label>
                    <select
                        v-model="form.assigned_to"
                        class="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option :value="null">{{ __('Todos los responsables') }}</option>
                        <option v-for="r in responsables" :key="r.id" :value="r.id">{{ r.name }}</option>
                    </select>
                </div>
                <button
                    type="button"
                    @click="clearFilters"
                    class="flex flex-col gap-1 px-4 py-2 bg-gray-100 text-gray-600 rounded text-sm hover:bg-gray-200 transition mt-auto"
                >
                    {{ __('Limpiar filtros') }}
                </button>
            </div>
        </div>

        <!-- ===== AVISO SOLO MIS INDICADORES ===== -->
        <div v-if="onlyMine" class="flex items-center gap-3 bg-amber-50 border border-amber-300 text-amber-800 rounded-md px-5 py-3 mb-5 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 flex-shrink-0 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <span><strong>Vista personal:</strong> Se están mostrando únicamente los indicadores de los tickets que tiene asignados.</span>
        </div>

        <!-- ===== INDICADORES ===== -->
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-5">

            <!-- 1. Indicador por Equipo (Plataforma) -->
            <div class="bg-white rounded-md shadow p-5">
                <div class="border-l-4 border-indigo-600 pl-3 mb-4">
                    <h2 class="text-base font-bold text-gray-800">{{ __('1. Indicador por Equipo (Plataforma)') }}</h2>
                    <span class="text-xs text-indigo-500 font-medium">{{ __('Requerido: Gráfico Circular') }}</span>
                </div>

                <div v-if="byEquipment.length > 0" class="flex flex-col md:flex-row items-center gap-6">
                    <div class="flex-shrink-0">
                        <pie-chart :data="byEquipment" :size="220" />
                    </div>
                    <div class="flex-1 w-full">
                        <table class="w-full text-sm mt-3">
                            <thead>
                                <tr class="bg-gray-50">
                                    <th class="text-left px-3 py-2 font-semibold text-gray-600">{{ __('Plataforma') }}</th>
                                    <th class="text-right px-3 py-2 font-semibold text-gray-600">{{ __('Cantidad') }}</th>
                                    <th class="text-right px-3 py-2 font-semibold text-gray-600">{{ __('Distribución %') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, i) in byEquipment" :key="i" class="border-t hover:bg-gray-50">
                                    <td class="px-3 py-2 flex items-center gap-2">
                                        <span class="inline-block w-3 h-3 rounded-full flex-shrink-0" :style="{ background: chartColors[i % chartColors.length] }"></span>
                                        {{ row.name || __('Sin clasificar') }}
                                    </td>
                                    <td class="px-3 py-2 text-right">{{ row.cantidad }}</td>
                                    <td class="px-3 py-2 text-right font-medium">{{ row.porcentaje }}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div v-else class="text-gray-400 text-sm py-6 text-center">{{ __('No found.') }}</div>
            </div>

            <!-- 2. Indicador por Sede -->
            <div class="bg-white rounded-md shadow p-5">
                <div class="border-l-4 border-indigo-600 pl-3 mb-4">
                    <h2 class="text-base font-bold text-gray-800">{{ __('2. Indicador por Sede') }}</h2>
                    <span class="text-xs text-indigo-500 font-medium">{{ __('Requerido: Gráfico Circular') }}</span>
                </div>

                <div v-if="bySede.length > 0" class="flex flex-col md:flex-row items-center gap-6">
                    <div class="flex-shrink-0">
                        <pie-chart :data="bySede" :size="220" />
                    </div>
                    <div class="flex-1 w-full">
                        <table class="w-full text-sm mt-1">
                            <thead>
                                <tr class="bg-gray-50">
                                    <th class="text-left px-3 py-2 font-semibold text-gray-600">{{ __('Sede') }}</th>
                                    <th class="text-right px-3 py-2 font-semibold text-gray-600">{{ __('Casos Atendidos') }}</th>
                                    <th class="text-right px-3 py-2 font-semibold text-gray-600">{{ __('Participación %') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, i) in bySede" :key="i" class="border-t hover:bg-gray-50">
                                    <td class="px-3 py-2 flex items-center gap-2">
                                        <span class="inline-block w-3 h-3 rounded-full flex-shrink-0" :style="{ background: chartColors[i % chartColors.length] }"></span>
                                        {{ row.name }}
                                    </td>
                                    <td class="px-3 py-2 text-right">{{ row.cantidad }}</td>
                                    <td class="px-3 py-2 text-right font-medium">{{ row.porcentaje }}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div v-else class="text-gray-400 text-sm py-6 text-center">{{ __('No found.') }}</div>
            </div>

            <!-- 3. Indicador por Sección (Tipo de Actividad) -->
            <div class="bg-white rounded-md shadow p-5">
                <div class="border-l-4 border-indigo-600 pl-3 mb-4">
                    <h2 class="text-base font-bold text-gray-800">{{ __('3. Indicador por Tipo de Soporte') }}</h2>
                    <span class="text-xs text-indigo-500 font-medium">{{ __('Requerido: Gráfico Circular') }}</span>
                </div>

                <div v-if="bySection.length > 0" class="flex flex-col md:flex-row items-center gap-6">
                    <div class="flex-shrink-0">
                        <pie-chart :data="bySection" :size="220" />
                    </div>
                    <div class="flex-1 w-full">
                        <table class="w-full text-sm mt-1">
                            <thead>
                                <tr class="bg-gray-50">
                                    <th class="text-left px-3 py-2 font-semibold text-gray-600">{{ __('Tipo de Actividad') }}</th>
                                    <th class="text-right px-3 py-2 font-semibold text-gray-600">{{ __('Frecuencia') }}</th>
                                    <th class="text-right px-3 py-2 font-semibold text-gray-600">{{ __('Porcentaje %') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, i) in bySection" :key="i" class="border-t hover:bg-gray-50">
                                    <td class="px-3 py-2 flex items-center gap-2">
                                        <span class="inline-block w-3 h-3 rounded-full flex-shrink-0" :style="{ background: chartColors[i % chartColors.length] }"></span>
                                        {{ row.name }}
                                    </td>
                                    <td class="px-3 py-2 text-right">{{ row.cantidad }}</td>
                                    <td class="px-3 py-2 text-right font-medium">{{ row.porcentaje }}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div v-else class="text-gray-400 text-sm py-6 text-center">{{ __('No found.') }}</div>
            </div>

            <!-- 4. Indicador por Tipo de Ticket -->
            <div class="bg-white rounded-md shadow p-5">
                <div class="border-l-4 border-indigo-600 pl-3 mb-4">
                    <h2 class="text-base font-bold text-gray-800">{{ __('4. Indicador por Tipo de Ticket') }}</h2>
                    <span class="text-xs text-indigo-500 font-medium">{{ __('Requerido: Gráfico Circular') }}</span>
                </div>

                <div v-if="byType.length > 0" class="flex flex-col md:flex-row items-center gap-6">
                    <div class="flex-shrink-0">
                        <pie-chart :data="byType" :size="220" />
                    </div>
                    <div class="flex-1 w-full">
                        <table class="w-full text-sm mt-1">
                            <thead>
                                <tr class="bg-gray-50">
                                    <th class="text-left px-3 py-2 font-semibold text-gray-600">{{ __('Tipo') }}</th>
                                    <th class="text-right px-3 py-2 font-semibold text-gray-600">{{ __('Cantidad') }}</th>
                                    <th class="text-right px-3 py-2 font-semibold text-gray-600">{{ __('Porcentaje %') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, i) in byType" :key="i" class="border-t hover:bg-gray-50">
                                    <td class="px-3 py-2 flex items-center gap-2">
                                        <span class="inline-block w-3 h-3 rounded-full flex-shrink-0" :style="{ background: chartColors[i % chartColors.length] }"></span>
                                        {{ row.name }}
                                    </td>
                                    <td class="px-3 py-2 text-right">{{ row.cantidad }}</td>
                                    <td class="px-3 py-2 text-right font-medium">{{ row.porcentaje }}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div v-else class="text-gray-400 text-sm py-6 text-center">{{ __('No found.') }}</div>
            </div>

        </div>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import { Head, Link } from '@inertiajs/inertia-vue3'
import PieChart from '@/Shared/PieChart'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

const CHART_COLORS = [
    '#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#14B8A6',
    '#6366F1', '#A3E635', '#FB923C', '#F43F5E', '#22D3EE',
]

export default {
    name: 'Indicadores',
    components: {
        Head,
        Link,
        PieChart,
    },
    layout: Layout,
    props: {
        filters:      { type: Object,  default: () => ({}) },
        onlyMine:     { type: Boolean, default: false },
        byEquipment:  { type: Array,   default: () => [] },
        bySede:       { type: Array,   default: () => [] },
        bySection:    { type: Array,   default: () => [] },
        byType:       { type: Array,   default: () => [] },
        departments:  { type: Array,   default: () => [] },
        priorities:   { type: Array,   default: () => [] },
        statuses:     { type: Array,   default: () => [] },
        types:        { type: Array,   default: () => [] },
        categories:   { type: Array,   default: () => [] },
        responsables: { type: Array,   default: () => [] },
    },
    data() {
        return {
            chartColors: CHART_COLORS,
            form: {
                fini:          this.filters.fini          ?? null,
                fend:          this.filters.fend          ?? null,
                department_id: this.filters.department_id ?? null,
                type_id:       this.filters.type_id       ?? null,
                priority_id:   this.filters.priority_id   ?? null,
                status_id:     this.filters.status_id     ?? null,
                category_id:   this.filters.category_id   ?? null,
                assigned_to:   this.filters.assigned_to   ?? null,
            },
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(
                    this.route('reports.indicators'),
                    pickBy(this.form),
                    { replace: true, preserveState: true }
                )
            }, 300),
        },
    },
    methods: {
        clearFilters() {
            this.form = {
                fini:          null,
                fend:          null,
                department_id: null,
                type_id:       null,
                priority_id:   null,
                status_id:     null,
                category_id:   null,
                assigned_to:   null,
            }
        },
    },
}
</script>
